import * as React from 'react';

import { TextIcon } from '@europrocurement/flexy-components';
import { FaOptionIcon, NamedIconsType } from '@europrocurement/l2d-icons';
import { Button, PaletteColor, SxProps, Typography, useTheme } from '@mui/material';

export type FlexyButtonProps = {
    onClick: React.MouseEventHandler<HTMLAnchorElement>;
    label?: string;
    color?: PaletteColor;
    icon?: NamedIconsType;
    disabled?: boolean;
    size?: 'small' | 'medium' | 'large';
};

export const FlexyButton: React.FunctionComponent<FlexyButtonProps> = function ({
    onClick,
    label,
    color,
    icon,
    disabled = false,
    size = 'small',
}: FlexyButtonProps) {
    const theme = useTheme();
    const mainColor = color || theme.palette.primary;
    const textColor = theme.palette.background.paper;

    // Tailles de bouton basées sur la propriété size
    const buttonSizes = {
        small: { button: 32, icon: 16, borderRadius: 6 },
        medium: { button: 40, icon: 20, borderRadius: 8 },
        large: { button: 48, icon: 24, borderRadius: 10 },
    };

    const sizeConfig = buttonSizes[size];

    const globalStyle: SxProps = {
        color: textColor,
        backgroundColor: mainColor.dark,
        verticalAlign: 'middle',
        ':hover': { backgroundColor: mainColor.main },
    };

    const specificStyle = React.useMemo<SxProps>(() => {
        if (label && !icon) {
            return {
                padding: '8px 12px',
                margin: '0 20px',
            };
        }

        if (!label && icon) {
            return {
                minWidth: 'unset',
                width: `${sizeConfig.button}px`,
                height: `${sizeConfig.button}px`,
                padding: 0,
                margin: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden', // Assure que rien ne dépasse
                borderRadius: `${sizeConfig.borderRadius}px`, // Bords plus arrondis
                lineHeight: 1, // Évite les problèmes de hauteur de ligne
                boxSizing: 'border-box', // Assure que padding est inclus dans la taille
            };
        }

        if (label && icon) {
            return {
                padding: '8px 12px',
                margin: '0 20px',
            };
        }

        return null;
    }, [icon, label, sizeConfig.borderRadius, sizeConfig.button]);

    const buttonContent = React.useMemo<React.ReactNode>(() => {
        if (label && !icon) {
            return <Typography>{label}</Typography>;
        }

        if (!label && icon) {
            return (
                <FaOptionIcon
                    {...icon.props}
                    size={undefined}
                    color={`${color}.main`}
                    optionColor={`${color}.dark`}
                    optionProps={{
                        ...icon.props.optionProps,
                    }}
                    iconProps={{
                        radius: 100,
                        ...icon.props.iconProps,
                        ...{
                            width: sizeConfig.icon,
                            height: sizeConfig.icon,
                        },
                    }}
                    sx={{
                        width: '32px',
                        height: '32px',
                        padding: 0,
                        margin: 0,
                    }}
                />
            );
        }

        if (label && icon) {
            return (
                <TextIcon
                    gap="8px"
                    icon={icon}
                >
                    {label}
                </TextIcon>
            );
        }

        return null;
    }, [color, icon, label, sizeConfig.icon]);

    return (
        <Button
            href=""
            size="small"
            variant="contained"
            onClick={onClick}
            disabled={disabled}
            sx={{
                ...globalStyle,
                ...specificStyle,
            }}
        >
            {buttonContent}
        </Button>
    );
};
